"use client";

import {
  DataTable,
  ErrorNotice,
  Notice,
  TextField,
} from "@/components/tool-kit/primitives";
import { CARD, HINT, LABEL, WIDGET_H2 } from "@/components/tool-kit/styles";
import {
  applyPad,
  cribDrag,
  inFives,
  letterDifference,
  padLetters,
} from "@/lib/tool-kit/ciphers-misc/one-time-pad";

const PLACEMENTS_SHOWN = 6;

export interface ReuseLabels {
  heading: string;
  intro: string;
  secondLabel: string;
  cribLabel: string;
  cribHint: string;
  cipherOne: string;
  cipherTwo: string;
  cipherDifference: string;
  messageDifference: string;
  same: string;
  cribHeading: string;
  cribIntro: string;
  colWhat: string;
  colLetters: string;
  colPosition: string;
  colReads: string;
  cribNone: string;
}

/**
 * What a reused pad gives away. Two messages are enciphered with the same pad letters;
 * subtracting the cipher texts cancels the pad, and a guessed word in one message then
 * shows what the other says in the same place.
 */
export function OneTimePadReuse({
  labels,
  messageOne,
  pad,
  start,
  second,
  onSecond,
  crib,
  onCrib,
  shortText,
  fmt,
}: {
  labels: ReuseLabels;
  messageOne: string;
  pad: string;
  start: number;
  second: string;
  onSecond: (value: string) => void;
  crib: string;
  onCrib: (value: string) => void;
  fmt: (value: number) => string;
  shortText: (count: number) => string;
}) {
  const messageTwo = padLetters(second);
  const cribLetters = padLetters(crib);
  const one = applyPad(messageOne, pad, start, 1);
  const two = applyPad(messageTwo, pad, start, 1);
  const short = Math.max(one.shortBy, two.shortBy);
  const ready = short === 0 && messageOne.length > 0 && messageTwo.length > 0;
  const cipherDifference = ready
    ? letterDifference(one.output, two.output)
    : "";
  const messageDifference = ready
    ? letterDifference(messageOne, messageTwo)
    : "";
  const placements =
    ready && cribLetters.length > 0
      ? cribDrag(cipherDifference, cribLetters).slice(0, PLACEMENTS_SHOWN)
      : [];

  return (
    <section className={`${CARD} space-y-4`}>
      <h2 className={WIDGET_H2}>{labels.heading}</h2>
      <p className="text-muted-foreground text-sm leading-relaxed">
        {labels.intro}
      </p>
      <TextField
        id="otp-second"
        label={labels.secondLabel}
        mono
        onChange={onSecond}
        value={second}
      />
      {short > 0 ? <ErrorNotice>{shortText(short)}</ErrorNotice> : null}
      {ready ? (
        <div className="space-y-4">
          <DataTable
            head={[labels.colWhat, labels.colLetters]}
            rows={[
              [labels.cipherOne, inFives(one.output)],
              [labels.cipherTwo, inFives(two.output)],
              [labels.cipherDifference, inFives(cipherDifference)],
              [labels.messageDifference, inFives(messageDifference)],
            ]}
          />
          {cipherDifference === messageDifference ? (
            <Notice>{labels.same}</Notice>
          ) : null}
          <div className="space-y-2">
            <h3 className={LABEL}>{labels.cribHeading}</h3>
            <p className={HINT}>{labels.cribIntro}</p>
            <TextField
              hint={labels.cribHint}
              id="otp-crib"
              label={labels.cribLabel}
              mono
              onChange={onCrib}
              value={crib}
            />
            {placements.length > 0 ? (
              <DataTable
                head={[labels.colPosition, labels.colReads]}
                rows={placements.map((placement) => [
                  fmt(placement.offset + 1),
                  placement.fragment,
                ])}
              />
            ) : (
              <p className={HINT}>{labels.cribNone}</p>
            )}
          </div>
        </div>
      ) : null}
    </section>
  );
}
